import React from 'react';
import { useTranslation } from 'react-i18next';

import { CONNECTOR_CATALOG } from '../constants/connector-configs';
import { ConnectorCatalogEntry, ConnectorType } from '../types';
import { ConnectorSidebarItem } from './ConnectorSidebarItem';

interface ConnectorSidebarProps {
  onAdd: (connector: ConnectorCatalogEntry) => void;
  /**
   * The connector the board is already built on, if any. Every flow on a board
   * shares one source, so once this is set the others cannot be used — which
   * the sidebar has to show rather than leave the vendor to discover by
   * clicking things that do nothing.
   */
  currentConnectorType: ConnectorType | null;
  /** Whether there is any flow type left to add. Two is the ceiling. */
  hasRoom: boolean;
}

export const ConnectorSidebar: React.FC<ConnectorSidebarProps> = ({
  onAdd,
  currentConnectorType,
  hasRoom,
}) => {
  const { t } = useTranslation();

  /** Why this connector cannot be added right now, or null when it can. */
  const unavailableReason = (type: ConnectorType): string | null => {
    if (currentConnectorType && type !== currentConnectorType) {
      return 'PAGES.API_INTEGRATION.ALREADY_USING_CONNECTOR';
    }
    if (currentConnectorType && !hasRoom) {
      return 'PAGES.API_INTEGRATION.BOTH_SYNC_TYPES_ADDED';
    }
    return null;
  };

  const ecommerceConnectors = CONNECTOR_CATALOG.filter(c => c.category === 'ecommerce');
  const otherConnectors = CONNECTOR_CATALOG.filter(c => c.category === 'other');

  const categoryLabelClass = 'mb-1 px-3 text-[11px] font-medium tracking-wide text-muted-foreground';

  return (
    <div
      data-testid="connector-sidebar"
      className="flex h-fit w-[260px] shrink-0 flex-col gap-4 rounded-xl border bg-background p-4"
    >
      <div className="px-3 pt-1">
        <p className="text-sm font-medium text-foreground">
          {t('PAGES.API_INTEGRATION.SIDEBAR_TITLE')}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('PAGES.API_INTEGRATION.DRAG_HINT')}
        </p>
      </div>

      {/* E-Commerce */}
      <div>
        <p className={categoryLabelClass}>
          {t('PAGES.API_INTEGRATION.CATEGORY_ECOMMERCE')}
        </p>
        <div className="flex flex-col gap-0.5">
          {ecommerceConnectors.map(connector => (
            <ConnectorSidebarItem
              key={connector.type}
              connector={connector}
              onAdd={onAdd}
              unavailableReasonKey={unavailableReason(connector.type)}
            />
          ))}
        </div>
      </div>

      {/* Other */}
      {otherConnectors.length > 0 && (
        <div>
          <p className={categoryLabelClass}>
            {t('PAGES.API_INTEGRATION.CATEGORY_OTHER')}
          </p>
          <div className="flex flex-col gap-0.5">
            {otherConnectors.map(connector => (
              <ConnectorSidebarItem
              key={connector.type}
              connector={connector}
              onAdd={onAdd}
              unavailableReasonKey={unavailableReason(connector.type)}
            />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
